import api from './api';
import type { CalendarDateResponse, CalendarMonthResponse } from '../types/calendar';

export const calendarApi = {
    /**
     * Get calendar info for a specific date
     * @param date - Date in YYYY-MM-DD format
     */
    async getDate(date: string): Promise<CalendarDateResponse> {
        const response = await api.get<CalendarDateResponse>(`/calendar/${date}`);
        return response.data;
    },

    /**
     * Get calendar info for an entire month
     * @param year - Year (YYYY)
     * @param month - Month (1-12)
     */
    async getMonth(year: number, month: number): Promise<CalendarMonthResponse> {
        const response = await api.get<CalendarMonthResponse>(`/calendar/month/${year}/${month}`);
        return response.data;
    },

    /**
     * Get calendar info for a date range
     * @param start - Start date (YYYY-MM-DD)
     * @param end - End date (YYYY-MM-DD)
     * @param limit - Max days to return
     */
    async getRange(start: string, end: string, limit = 365) {
        const response = await api.get('/calendar/range', {
            params: { start, end, limit },
        });
        return response.data;
    },

    /**
     * Find solar date for a given lunar date in a specific year
     * Scans through months to find matching lunar day/month
     * @param lunarDay - Lunar day (1-30)
     * @param lunarMonth - Lunar month (1-12)
     * @param solarYear - Solar year to search in
     * @returns Solar date string (YYYY-MM-DD) or null if not found
     */
    async lunarToSolar(lunarDay: number, lunarMonth: number, solarYear: number): Promise<string | null> {
        try {
            // Lunar month X usually falls around solar month X, X+1, or X+2
            // Lunar month 1 (Tết) typically falls in solar Jan-Feb
            const searchMonths = [lunarMonth, lunarMonth + 1, lunarMonth + 2]
                .filter(m => m >= 1 && m <= 12);

            for (const month of searchMonths) {
                const monthData = await this.getMonth(solarYear, month);
                if (monthData?.days) {
                    for (const day of monthData.days) {
                        if (day.lunar.day === lunarDay && day.lunar.month === lunarMonth) {
                            return day.date; // Found it!
                        }
                    }
                }
            }
            return null;
        } catch (error) {
            console.error('Failed to convert lunar to solar:', error);
            return null;
        }
    },
};

export default calendarApi;
